import React from 'react';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {useControllerProps} from '../../../../../domain/controllers/ControllerContext';
import s from './OrderSummary.scss';
import {SubTotals} from './SubTotals/SubTotals';
import {CouponTotal} from '../../../../../common/components/CouponTotal/CouponTotal';
import {Total} from './Total/Total';
import {PaymentBreakdown} from './PaymentBreakdown/PaymentBreakdown';
import {Spinner, SpinnerTypes} from 'wix-ui-tpa';
import {Violations} from '../../../../../common/components/Violations/Violations';
import {MinimumOrderAmount} from '../MinimumOrderAmount/MinimumOrderAmount';
import {CheckoutButtons} from '../CheckoutButtons/CheckoutButtons';
import {SecureCheckout} from '../../../../../common/components/SecureCheckout/SecureCheckout';
import {CartBodyDataHook} from '../../CartApp/partials/CartBody';
import {useIsAfterHydration} from '../../../../../hooks/useIsAfterHydration';
import {EstimateTotalsErrorNotification} from '../../../../../common/components/EstimateTotalsErrorNotification/EstimateTotalsErrorNotification';
import {CartSummarySkeleton} from './CartSummarySkeleton/CartSummarySkeleton';

export enum OrderSummaryDataHook {
  SubTotalsRoot = 'SubTotals.root',
  SubTotalsValue = 'SubTotals.subtotalText',
  EstimatedTaxRoot = 'EstimatedTax.root',
  EstimatedTaxShippingDestinationWithTitle = 'EstimatedTax.shippingDestinationWithTitle',
  EstimatedTaxTaxName = 'EstimatedTax.taxName',
  EstimatedTaxEstimatedTaxPrice = 'EstimatedTax.estimatedTaxPrice',
  TotalShippingRoot = 'TotalShipping.root',
  TotalShippingShippingDestinationWithTitle = 'TotalShipping.shippingDestinationWithTitle',
  TotalShippingTitle = 'TotalShipping.title',
  TotalShippingEstimatedShipping = 'TotalShipping.estimatedShipping',
  ShippingDestinationRoot = 'ShippingDestination.shippingDestination',
  ShippingDestinationTaxRoot = 'ShippingDestination.taxShippingDestination',
  ShippingDestinationChangeRegion = 'ShippingDestination.changeRegion',
  DeliveryMethodRoot = 'DeliveryMethod.root',
  DeliveryMethodDropdown = 'DeliveryMethod.dropdown',
  DeliveryMethodDropdownDescription = 'DeliveryMethod.dropdown-description',
  DeliveryErrorRoot = 'DeliveryError.root',
  DeliveryErrorMessage = 'DeliveryError.error',
  DeliveryNotificationRoot = 'DeliveryNotification.root',
  DeliveryNotificationMessage = 'DeliveryNotification.message',
  TotalTitle = 'Total.title',
  TotalFormattedValue = 'Total.formattedValue',
  TotalCurrencyConverterDisclaimer = 'Total.currencyConverterDisclaimer',
  TotalTaxIncludedLabel = 'Total.taxIncludedLabel',
  TotalCartDisclaimer = 'Total.cartDisclaimer',
  SingleAdditionalFeeRoot = 'SingleAdditionalFee.root',
  MultipleAdditionalFeesRoot = 'MultipleAdditionalFees.root',
  AdditionalFeesTitle = 'AdditionalFees.title',
  AdditionalFeesName = 'AdditionalFees.name',
  AdditionalFeesRoot = 'AdditionalFees.root',
  AdditionalFeesPrice = 'AdditionalFees.price',
  PaymentBreakdownRoot = 'PaymentBreakdown.root',
  PayNowTitle = 'PayNowLater.payNowTitle',
  PayNowValue = 'Total.payNowValue',
  PayLaterTitle = 'PayLaterLater.payLaterTitle',
  PayLaterValue = 'Total.payLaterValue',
  Loader = 'OrderSummary.Loader',
  PayAfterFreeTrialTitle = 'PayAfterFreeTrial.title',
  PayAfterFreeTrialValue = 'PayAfterFreeTrial.value',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const OrderSummary = () => {
  const {t} = useTranslation();
  const {orderStore, cartStore} = useControllerProps();
  const {shouldDisplaySubtotal, shouldDisplayPayNowPayLater} = orderStore;
  const {
    cartSummary,
    cartModel,
    isSummaryUpdating,
    shouldShowSecureCheckout,
    shouldDisplayViolations,
    shouldShowMinimumOrderAmount,
    shouldEstimateTotals,
    isSummaryLoading,
    mapCartSummaryColors,
    errorOnEstimateTotals,
    refactorCouponOnCart,
  } = cartStore;
  const appliedCoupon = refactorCouponOnCart
    ? cartSummary?.couponDiscount && cartModel?.coupon
    : cartSummary?.appliedCoupon || cartModel?.coupon;

  const isAfterHydration = useIsAfterHydration();

  const showAsideLoader = isSummaryLoading || (!isAfterHydration && shouldEstimateTotals);

  if (errorOnEstimateTotals) {
    return <EstimateTotalsErrorNotification />;
  }
  if (showAsideLoader) {
    return <CartSummarySkeleton />;
  }

  return (
    <div
      data-hook={CartBodyDataHook.CartAsideContent}
      className={mapCartSummaryColors ? s.cartSummary : /* istanbul ignore next */ undefined}>
      {isSummaryUpdating && <Spinner data-hook={OrderSummaryDataHook.Loader} isCentered type={SpinnerTypes.slim} />}
      <div className={isSummaryUpdating ? s.loading : undefined}>
        {shouldDisplaySubtotal && <SubTotals />}
        {appliedCoupon && <CouponTotal />}
        <Total />
        {shouldDisplayPayNowPayLater && <PaymentBreakdown />}
      </div>
      {shouldDisplayViolations && <Violations />}
      {shouldShowMinimumOrderAmount && <MinimumOrderAmount />}
      <CheckoutButtons />
      {shouldShowSecureCheckout && <SecureCheckout text={t('cart.orderSummary.secureCheckout.label')} />}
    </div>
  );
};
